import { Funnel, MagnifyingGlass } from "@phosphor-icons/react";
import { forwardRef } from "react";
import { cn } from "@/lib/cn";
import { Badge } from "@/components/primitives/Badge";
import { Button } from "@/components/primitives/Button";
import { Chip } from "@/components/primitives/Chip";
import { Input } from "@/components/primitives/Input";
import type { FilterBarProps } from "./FilterBar.types";

export const FilterBar = forwardRef<HTMLDivElement, FilterBarProps>(
  (
    {
      className,
      searchValue,
      onSearchChange,
      onClearSearch,
      searchPlaceholder = "Search",
      searchInput,
      filterTrigger,
      onOpenFilters,
      activeFiltersCount = 0,
      filterChips = [],
      onClearAllFilters,
      primaryAction,
      secondaryActions,
      sticky,
      children,
      ...props
    },
    ref
  ) => (
    <div ref={ref} className={cn("bfrs-border-b bfrs-border-border bfrs-bg-[#fbfcfb]", sticky && "bfrs-sticky bfrs-top-0 bfrs-z-30", className)} {...props}>
      <div className="bfrs-flex bfrs-flex-wrap bfrs-items-center bfrs-gap-3 bfrs-px-5 bfrs-py-2.5">
        {searchInput ??
          (onSearchChange && (
            <Input
              size="sm"
              value={searchValue ?? ""}
              onChange={(event) => onSearchChange(event.target.value)}
              onClear={onClearSearch}
              placeholder={searchPlaceholder}
              leftIcon={<MagnifyingGlass size={14} />}
              containerClassName="bfrs-w-full sm:bfrs-w-64"
              aria-label={searchPlaceholder}
            />
          ))}
        {children}
        {filterTrigger ??
          (onOpenFilters && (
            <Button variant="secondary" size="sm" leftIcon={<Funnel size={13} />} onClick={onOpenFilters}>
              More Filters
              {activeFiltersCount > 0 && <Badge tone="primary" count={activeFiltersCount} className="bfrs-ml-1" />}
            </Button>
          ))}
        <div className="bfrs-min-w-2 bfrs-flex-1" />
        {secondaryActions}
        {primaryAction}
      </div>
      {(filterChips.length > 0 || activeFiltersCount > 0) && (
        <div className="bfrs-flex bfrs-flex-wrap bfrs-items-center bfrs-gap-2 bfrs-border-t bfrs-border-border bfrs-bg-surface-muted/50 bfrs-px-5 bfrs-py-2">
          {filterChips.map((chip) => (
            <Chip key={chip.id} tone="neutral" onRemove={chip.onRemove}>
              {chip.label}
            </Chip>
          ))}
          {filterChips.length === 0 && activeFiltersCount > 0 && <Chip tone="neutral">{activeFiltersCount} filters applied</Chip>}
          {onClearAllFilters && (
            <Button variant="link" size="sm" onClick={onClearAllFilters}>
              Clear all
            </Button>
          )}
        </div>
      )}
    </div>
  )
);

FilterBar.displayName = "FilterBar";
